import { getCurrentUserInfo } from "@/services/accessControlApi";
import { AuthState, User } from "@/types/auth";
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";

interface AuthContextValue extends AuthState {
  signInWithAzureAD: () => Promise<void>;
  signOutUser: () => Promise<void>;
  refreshSession: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const checkAuthStatus = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session.tokens) {
        const idToken = session.tokens.idToken;
        const payload = idToken?.payload;
        const accessToken = session.tokens.accessToken?.toString();

        // Fetch full user info from access-control API
        const userInfo = accessToken ? await getCurrentUserInfo(accessToken) : null;

        // Extract name from cognito:username as fallback
        const cognitoUsername = payload?.["cognito:username"] as string;
        const extractedName = cognitoUsername
          ? cognitoUsername.split("_")[1]?.split("@")[0]
          : (payload?.email as string)?.split("@")[0];

        const user: User = {
          userId: payload?.sub as string,
          email: payload?.email as string,
          firstName: userInfo?.firstName,
          lastName: userInfo?.lastName,
          name:
            userInfo?.firstName && userInfo?.lastName
              ? `${userInfo.firstName} ${userInfo.lastName}`
              : extractedName || (payload?.name as string),
          tenantId: userInfo?.tenantId || (payload?.["custom:tenant_id"] as string),
          sicId: userInfo?.sicId || (payload?.["custom:sic_id"] as string),
          permissions:
            userInfo?.permissions ||
            (payload?.["custom:permissions"]
              ? JSON.parse(payload["custom:permissions"] as string)
              : []),
        };

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      // User is not authenticated - this is not an error state, just unauthenticated
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for auth events
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          checkAuthStatus();
          break;
        case "signInWithRedirect_failure":
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            error: "Authentication failed. Please try again.",
          }));
          break;
        case "customOAuthState":
          checkAuthStatus();
          break;
        case "signedIn":
        case "tokenRefresh":
          checkAuthStatus();
          break;
        case "signedOut":
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          break;
        case "tokenRefresh_failure":
          setAuthState((prev) => ({
            ...prev,
            error: "Session expired. Please sign in again.",
          }));
          break;
      }
    });

    return unsubscribe;
  }, []);

  const signInWithAzureAD = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      // Trigger OAuth redirect to Azure AD via Cognito hosted UI
      // Use provider parameter to specify Azure AD instead of default COGNITO
      await signInWithRedirect({ provider: { custom: "AzureAD" } });
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Sign in error",
      }));
      throw error;
    }
  };

  const signOutUser = async () => {
    try {
      await signOut();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Sign out error",
      }));
      throw error;
    }
  };

  const refreshSession = async () => {
    await fetchAuthSession({ forceRefresh: true });
    await checkAuthStatus();
  };

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      return undefined;
    }
  };

  const value: AuthContextValue = {
    ...authState,
    signInWithAzureAD,
    signOutUser,
    refreshSession,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
